import React from "react";
import { Guid } from "guid-typescript";
import { createPortal } from "react-dom";

import { DropDownOption } from "./DropDown";

import "./TimePicker.scss";
import Utility from "../../foundation/Utility";

interface TimePickerProps {
    id?: string;
    label?: string;
    value?: Date;
    width?: number | string;
    minTime?: Date;
    maxTime?: Date;
    disabled?: boolean;
    className?: string;
    tooltip?: string;
    tooltipPlace?: string;
    onChange?: (value: Date) => void;
}

interface TimePickerState {
    ref: React.RefObject<HTMLLabelElement>;
    inputRef: React.RefObject<HTMLInputElement>;
    anchor: string;
    open: boolean;
    focused: boolean;
    current: Date;
    displayValue: string;
    segment: "hour" | "minute" | "period";
    times: DropDownOption[];
}

class TimePicker extends React.Component<TimePickerProps, TimePickerState> {
    state: TimePickerState = {
        ref: React.createRef<HTMLLabelElement>(),
        inputRef: React.createRef<HTMLInputElement>(),
        anchor: `--${Guid.create().toString()}`,
        open: false,
        focused: false,
        current: new Date(),
        displayValue: "",
        segment: "hour",
        times: [],
    };

    componentDidMount(): void {
        if (this.props.value) {
            const value = this.props.value || new Date();
            this.setState(
                {
                    current: value,
                    displayValue: this.formatTime(value),
                },
                this.buildTimes.bind(this),
            );
        }
    }

    componentDidUpdate(
        prevProps: Readonly<TimePickerProps>,
        prevState: Readonly<TimePickerState>,
        snapshot?: any,
    ): void {
        if (prevProps.value !== this.props.value) {
            const value = this.props.value || new Date();
            this.setState(
                {
                    current: value,
                    displayValue: this.formatTime(value),
                },
                this.buildTimes.bind(this),
            );

            if (this.state.open) this.open();
        }
        if (prevState.current !== this.state.current) {
            const value = this.state.current || new Date();
            this.setState({
                current: value,
                displayValue: this.formatTime(value),
            });

            this.setSelection(
                false,
                this.state.segment === "hour"
                    ? 0
                    : this.state.segment === "minute"
                    ? 3
                    : 6,
                "right",
            );
        }
    }

    formatTime(date: Date, noPeriod: boolean = false): string {
        return date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: !noPeriod,
        });
    }

    buildTimes() {
        const times = [];
        let current = new Date(this.state.current).dateOnly();
        const next = new Date(current).addMinutes(60 * 12);

        while (current < next) {
            times.push({
                value: current,
                text: this.formatTime(current),
            });
            current = current.addMinutes(30);
        }

        this.setState({ times });
    }

    open() {
        this.setState({
            open: true,
        });
    }

    close() {
        this.setState({ open: false });
    }

    toggle() {
        if (this.props.disabled) return;
        if (this.state.open) this.close();
        else this.open();
    }

    handleFocus() {
        setTimeout(() => {
            this.setState({ focused: true });
        });
    }

    handleInputFocus(
        before: boolean = false,
        direction: "left" | "right" = "left",
    ) {
        this.handleFocus();

        this.setSelection(before, null, direction);
    }

    opId: string;
    handleBlur() {
        const opId = Guid.create().toString();
        this.opId = opId;
        this.setState({ focused: false });
        setTimeout(() => {
            if (!this.state.focused && this.opId === opId) {
                this.close();
            }
        }, 200);
    }

    handleClick(_event: React.MouseEvent<HTMLInputElement>) {
        if (this.props.disabled) return;
        this.handleInputFocus(false, "left");
        this.open();
    }

    handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case "ArrowLeft":
                this.handleInputFocus(true, "left");
                event.stopPropagation();
                event.preventDefault();
                break;
            case "ArrowUp":
            case "ArrowDown":
            case "ArrowRight":
                this.handleInputFocus(true, "right");
                event.stopPropagation();
                event.preventDefault();
                break;
            case "0":
            case "1":
            case "2":
            case "3":
            case "4":
            case "5":
            case "6":
            case "7":
            case "8":
            case "9":
                if (this.state.segment === "period") {
                    event.stopPropagation();
                    event.preventDefault();
                }
                break;
            case "a":
            case "A":
            case "p":
            case "P":
            case "m":
            case "M":
                if (this.state.segment !== "period") {
                    event.stopPropagation();
                    event.preventDefault();
                }
                break;
            case "Tab":
                break;
            default:
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    handleKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case "Enter":
            case " ":
                this.toggle();
                event.stopPropagation();
                event.preventDefault();
                break;
            case "Escape":
                this.close();
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    timeHandleKeyUp(event: React.KeyboardEvent<HTMLLIElement>, time: Date) {
        switch (event.key) {
            case "Enter":
            case " ":
                this.setTime(time);
                event.stopPropagation();
                event.preventDefault();
                break;
            case "Escape":
                this.close();
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    inputChange(event: React.ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        this.setState({ displayValue: value });

        this.inputParse();
    }

    inputParse = Utility.debounce(() => {
        const mainParts = this.state.displayValue.split(" ");
        const timeParts = mainParts[0].split(":");

        const hours = Math.max(1, Math.min(parseInt(timeParts[0]), 12));
        const minutes = Math.max(0, Math.min(parseInt(timeParts[1]), 59));
        let date = new Date(this.state.current);
        date.setHours(
            ["p", "pm"].includes(mainParts[1]?.toLowerCase())
                ? hours + 12
                : hours,
        );
        date.setMinutes(minutes);

        this.setState({ current: date });
    }, 300);

    setAm() {
        let temp = new Date(this.state.current);
        if (temp.getHours() >= 12) temp.setHours(temp.getHours() - 12);

        if (!!this.props.minTime && temp.compare(this.props.minTime) < 0)
            temp = new Date(this.props.minTime);
        else if (!!this.props.maxTime && temp.compare(this.props.maxTime) > 0)
            temp = new Date(this.props.maxTime);

        this.setState({
            current: temp,
            displayValue: this.formatTime(temp),
        });

        if (this.props.onChange) this.props.onChange(temp);
    }

    setPm() {
        let temp = new Date(this.state.current);
        if (temp.getHours() < 12) temp.setHours(temp.getHours() + 12);

        if (!!this.props.minTime && temp.compare(this.props.minTime) < 0)
            temp = new Date(this.props.minTime);
        else if (!!this.props.maxTime && temp.compare(this.props.maxTime) > 0)
            temp = new Date(this.props.maxTime);

        this.setState({
            current: temp,
            displayValue: this.formatTime(temp),
        });

        if (this.props.onChange) this.props.onChange(temp);
    }

    setTime(time: Date) {
        if (!this.timeValid(time)) return;

        let temp = new Date(time);
        if (this.state.current.getHours() >= 12)
            temp.setHours(temp.getHours() + 12);

        if (!!this.props.minTime && temp.compare(this.props.minTime) < 0)
            temp = new Date(this.props.minTime);
        else if (!!this.props.maxTime && temp.compare(this.props.maxTime) > 0)
            temp = new Date(this.props.maxTime);

        this.setState({
            current: temp,
            displayValue: this.formatTime(temp),
            open: false,
        });

        if (this.props.onChange) this.props.onChange(temp);
    }

    setSelection(
        before: boolean = false,
        pos: number,
        direction: "left" | "right",
    ) {
        setTimeout(() => {
            const input = this.state.inputRef.current;
            if (!input) return;

            pos = pos ?? input.selectionStart ?? 0;

            if (before)
                pos = Math.max(
                    0,
                    Math.min(8, direction === "left" ? pos! - 2 : pos! + 2),
                );

            let seg: "hour" | "minute" | "period" = "hour";
            switch (direction) {
                case "left":
                    if (pos < 4) {
                        input.setSelectionRange(0, 2);
                        seg = "hour";
                    } else if (pos < 7) {
                        input.setSelectionRange(3, 5);
                        seg = "minute";
                    } else {
                        input.setSelectionRange(6, 8);
                        seg = "period";
                    }
                    break;
                case "right":
                    if (pos < 2) {
                        input.setSelectionRange(0, 2);
                        seg = "hour";
                    } else if (pos < 5) {
                        input.setSelectionRange(3, 5);
                        seg = "minute";
                    } else {
                        input.setSelectionRange(6, 8);
                        seg = "period";
                    }
                    break;
            }

            this.setState({ segment: seg });
        });
    }

    isSelected(time: Date) {
        const temp = new Date(this.state.current);
        if (temp.getHours() >= 12) temp.setHours(temp.getHours() - 12);
        return (
            time.getHours() === temp.getHours() &&
            time.getMinutes() === temp.getMinutes()
        );
    }

    timeValid(time: Date): boolean {
        const temp = new Date(time);
        if (this.state.current.getHours() >= 12)
            temp.setHours(temp.getHours() + 12);

        if (!!this.props.minTime && temp.compare(this.props.minTime) < 0)
            return false;
        else if (!!this.props.maxTime && temp.compare(this.props.maxTime) > 0)
            return false;

        return true;
    }

    render(): React.ReactNode {
        const rect = this.state.ref.current?.getBoundingClientRect();
        const isTop = (rect ? rect.top / window.innerHeight : 0) > 0.5;

        const maxHeight = Math.min(
            300,
            isTop
                ? rect?.top - 5
                : window.innerHeight - (rect?.bottom ?? 0) - 3,
        );

        let classes = "time-picker";

        if (this.props.className) classes += ` ${this.props.className}`;
        if (this.props.disabled) classes += " disabled";

        return (
            <label
                ref={this.state.ref}
                className={classes}
                style={{
                    width: this.props.width,
                    anchorName: this.state.anchor,
                }}
                data-html={true}
                data-tip={this.props.tooltip}
                data-place={this.props.tooltipPlace}>
                <input
                    type="text"
                    ref={this.state.inputRef}
                    disabled={this.props.disabled}
                    value={this.state.displayValue}
                    onFocus={() => this.handleInputFocus(false, "right")}
                    onBlur={this.handleBlur.bind(this)}
                    onClick={this.handleClick.bind(this)}
                    onKeyDown={this.handleKeyDown.bind(this)}
                    onKeyUp={this.handleKeyUp.bind(this)}
                    onChange={this.inputChange.bind(this)}
                />
                {this.props.label && (
                    <span className="text">{this.props.label}</span>
                )}
                {this.state.open &&
                    createPortal(
                        <div
                            className={`times-display${isTop ? " top" : ""}`}
                            style={{
                                positionAnchor: this.state.anchor,
                                maxHeight: `${maxHeight}px`,
                            }}>
                            <div className="time-selection">
                                <ul className="times">
                                    {this.state.times.map((time, index) => (
                                        <li
                                            key={index}
                                            tabIndex={
                                                this.timeValid(
                                                    time.value as Date,
                                                )
                                                    ? 0
                                                    : -1
                                            }
                                            className={`${
                                                this.isSelected(
                                                    time.value as Date,
                                                )
                                                    ? " selected"
                                                    : ""
                                            }${
                                                !this.timeValid(
                                                    time.value as Date,
                                                )
                                                    ? " invalid"
                                                    : ""
                                            }`}
                                            onFocus={this.handleFocus.bind(
                                                this,
                                            )}
                                            onBlur={this.handleBlur.bind(this)}
                                            onKeyUp={event =>
                                                this.timeHandleKeyUp(
                                                    event,
                                                    time.value as Date,
                                                )
                                            }
                                            onClick={this.setTime.bind(
                                                this,
                                                time.value as Date,
                                            )}>
                                            {this.formatTime(time.value, true)}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="period-buttons">
                                <button
                                    tabIndex={0}
                                    className={`${
                                        this.state.current.getHours() < 12
                                            ? "selected"
                                            : ""
                                    }`}
                                    onFocus={this.handleFocus.bind(this)}
                                    onClick={this.setAm.bind(this)}>
                                    AM
                                </button>
                                <button
                                    tabIndex={0}
                                    className={`${
                                        this.state.current.getHours() >= 12
                                            ? "selected"
                                            : ""
                                    }`}
                                    onFocus={this.handleFocus.bind(this)}
                                    onClick={this.setPm.bind(this)}>
                                    PM
                                </button>
                            </div>
                        </div>,
                        document.querySelector(".page-elements"),
                    )}
            </label>
        );
    }
}

export default TimePicker;
